import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { fmtDate, todayStr, OPERATION_TYPES, EXEC_STATUS } from "../lib/constants";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import {
  Navigation, Phone, MapPin, Clock, CheckCircle2, Camera, WifiOff, AlertTriangle,
  Building2, Package, ChevronDown, ChevronUp, Sparkles,
} from "lucide-react";

const CACHE_KEY = "lm_driver_tours";

export default function DriverView() {
  const { user } = useAuth();
  const [tours, setTours] = useState([]);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [exec, setExec] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  const flushPending = useCallback(async () => {
    const q = JSON.parse(localStorage.getItem("lm_pending_exec") || "[]");
    if (q.length === 0) { setPendingCount(0); return; }
    const remaining = [];
    for (const item of q) {
      try { await api.patch(`/requests/${item.id}/exec`, item.body); }
      catch (err) {
        const st = err?.response?.status;
        if (st !== 404 && st !== 410) remaining.push(item);
      }
    }
    localStorage.setItem("lm_pending_exec", JSON.stringify(remaining));
    setPendingCount(remaining.length);
    if (remaining.length === 0 && q.length > 0) toast.success(`${q.length} aggiornamenti offline inviati`);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/my/tours");
      setTours(data);
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) setTours(JSON.parse(cached));
    }
  }, []);

  useEffect(() => {
    setPendingCount(JSON.parse(localStorage.getItem("lm_pending_exec") || "[]").length);
    if (navigator.onLine) flushPending().then(load); else load();
    const on = async () => { setOffline(false); await flushPending(); load(); };
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    const t = setInterval(() => { if (navigator.onLine) { flushPending(); load(); } }, 25000);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); clearInterval(t); };
  }, [load, flushPending]);

  const today = todayStr();
  const todayTours = tours.filter((t) => t.date === today);
  const weekTours = tours.filter((t) => t.date !== today);

  const ack = async (t) => { try { await api.post(`/tours/${t.id}/ack-changes`); load(); } catch {} };

  return (
    <div className="max-w-2xl mx-auto space-y-5 pb-10">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase text-lm-muted font-semibold">Ciao {user?.name?.split(" ")[0]}</p>
        <h1 className="font-display text-3xl text-lm-text">I miei giri</h1>
      </div>

      {offline && (
        <div className="bg-[#FEF8EC] border border-[#F3D295] rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-[#9A6B00]" data-testid="offline-banner">
          <WifiOff className="w-5 h-5" /> Sei offline: stai vedendo l'ultima versione salvata. Gli aggiornamenti verranno inviati al ritorno della rete.
        </div>
      )}
      {!offline && pendingCount > 0 && (
        <div className="bg-[#EBF3FA] border border-[#96C2EC] rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-[#1D4ED8]" data-testid="pending-sync-banner">
          <WifiOff className="w-5 h-5" /> {pendingCount} aggiornamenti in coda: invio in corso al ritorno della rete…
        </div>
      )}

      <section>
        <h2 className="font-display text-xl text-lm-brown mb-3">Oggi · {fmtDate(today)}</h2>
        {todayTours.length === 0 ? <p className="text-lm-muted text-sm lm-card p-6 text-center">Nessun giro assegnato per oggi.</p>
          : todayTours.map((t) => <TourBlock key={t.id} tour={t} onExec={setExec} onAck={ack} onChanged={load} offline={offline} />)}
      </section>

      {weekTours.length > 0 && (
        <section>
          <h2 className="font-display text-xl text-lm-brown mb-3">Prossimi giri</h2>
          {weekTours.map((t) => <TourBlock key={t.id} tour={t} onExec={setExec} onAck={ack} onChanged={load} offline={offline} collapsed />)}
        </section>
      )}

      {exec && <ExecDialog op={exec} onClose={() => setExec(null)} onDone={load} offline={offline} />}
    </div>
  );
}

function TourBlock({ tour, onExec, onAck, onChanged, offline, collapsed = false }) {
  const [open, setOpen] = useState(!collapsed);

  const setExecStatus = async (o, exec_status) => {
    try {
      await api.patch(`/requests/${o.id}/exec`, { exec_status, photos: [] });
      toast.success(exec_status === "completata" ? "✓ Completata" : "Spunta annullata");
      onChanged();
    } catch (e) {
      if (offline) {
        const q = JSON.parse(localStorage.getItem("lm_pending_exec") || "[]");
        q.push({ id: o.id, body: { exec_status, photos: [] } });
        localStorage.setItem("lm_pending_exec", JSON.stringify(q));
        toast.message("Salvato offline, verrà inviato al ritorno della rete");
        onChanged();
      } else toast.error(apiErr(e));
    }
  };

  return (
    <div className="lm-card p-4 mb-3" data-testid={`driver-tour-${tour.code}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between">
        <div className="text-left">
          <div className="flex items-center gap-2"><span className="font-display text-lg text-lm-brown">{tour.code}</span><StatusBadge status={tour.status} /></div>
          <p className="text-xs text-lm-muted">{fmtDate(tour.date)} · {tour.vehicle?.identifier || "mezzo n.d."} · {tour.stops?.length || 0} tappe · {tour.total_km || 0} km</p>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-lm-muted" /> : <ChevronDown className="w-5 h-5 text-lm-muted" />}
      </button>

      {tour.has_changes_after_publish && (
        <div className="mt-3 bg-[#EBF3FA] border border-[#96C2EC] rounded-xl px-3 py-2.5 flex items-center justify-between gap-2" data-testid="changes-banner">
          <span className="text-sm text-[#1D4ED8] flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Il giro è stato modificato dopo la pubblicazione.</span>
          <Button size="sm" onClick={() => onAck(tour)} className="bg-[#1D4ED8] hover:bg-[#1740b0] text-white h-8">Ho capito</Button>
        </div>
      )}

      {open && (
        <div className="mt-3 space-y-3">
          {tour.ai_summary && (
            <div className="rounded-xl bg-[#F4ECE1] border border-lm-sand p-3" data-testid={`driver-ai-note-${tour.code}`}>
              <p className="text-xs font-bold uppercase tracking-wider text-lm-brown mb-1 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Nota di viaggio</p>
              <p className="text-sm text-lm-text2 whitespace-pre-line leading-relaxed">{tour.ai_summary}</p>
            </div>
          )}
          {(tour.stops || []).map((s, i) => (
            <div key={s.id || i} className="rounded-xl border border-lm-sandsoft bg-white overflow-hidden">
              <div className="bg-lm-sandsoft/50 px-4 py-3">
                <div className="flex items-start gap-3">
                  <span className="w-8 h-8 shrink-0 rounded-full bg-lm-brown text-lm-cream font-bold flex items-center justify-center">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-lm-text">{s.label}</p>
                    <a data-testid={`addr-link-${i}`} href={s.lat != null ? `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address || "")}`}
                      target="_blank" rel="noreferrer" className="text-sm text-lm-terra underline decoration-lm-sand flex items-center gap-1 hover:text-lm-brown">
                      <MapPin className="w-4 h-4 shrink-0" /> {s.address || "—"}
                    </a>
                    {s.eta_arrive && <p className="text-sm text-lm-terra mt-0.5"><Clock className="w-4 h-4 inline mr-1" />Arrivo previsto {s.eta_arrive}</p>}
                    {s.appointment && (
                      <p className={`text-sm mt-0.5 font-semibold flex items-center gap-1 ${s.appointment_late ? "text-[#991B1B]" : "text-[#5B3E86]"}`}>
                        <Clock className="w-4 h-4 shrink-0" /> Appuntamento tassativo {s.appointment}
                        {s.appointment_late ? ` · ⚠ possibile ritardo (+${s.appointment_late_min} min)` : ""}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <a data-testid={`nav-btn-${i}`} href={s.lat != null ? `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address || "")}`}
                    target="_blank" rel="noreferrer" className="flex-1 h-12 rounded-xl bg-lm-brown text-lm-cream font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform">
                    <Navigation className="w-5 h-5" /> Naviga
                  </a>
                  {(s.operations?.[0]?.contact_phone) && (
                    <a href={`tel:${s.operations[0].contact_phone}`} className="flex-1 h-12 rounded-xl border-2 border-lm-brown text-lm-brown font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform">
                      <Phone className="w-5 h-5" /> Chiama
                    </a>
                  )}
                </div>
              </div>
              <div className="p-3 space-y-2.5">
                {(s.operations || []).map((o) => (
                  <div key={o.id} data-testid={`op-${o.code}`} className="border border-lm-sandsoft rounded-xl p-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-mono text-sm font-bold text-lm-brown">{o.code}</span>
                      <StatusBadge status={o.exec_status || "da_effettuare"} exec testid={`op-status-${o.code}`} />
                    </div>
                    <p className="text-sm font-semibold text-lm-text mt-1">{OPERATION_TYPES[o.operation_type]} · {o.goods_description || "merce"}</p>
                    <div className="text-xs text-lm-muted mt-1 space-y-0.5">
                      <p className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {o.azienda}{o.commessa ? ` · ${o.commessa}` : ""} · Rich. {o.requester_name || "—"}</p>
                      {o.packages_count != null && <p className="flex items-center gap-1"><Package className="w-3.5 h-3.5" /> {o.packages_count} colli {o.weight_kg ? `· ${o.weight_kg} kg` : ""}</p>}
                      {o.notes && <p className="text-[#9A6B00] bg-[#FEF8EC] border border-[#F3D295] rounded px-2 py-1 mt-1">📝 {o.notes}</p>}
                    </div>
                    {(o.photos || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`op-photos-${o.code}`}>
                        {o.photos.map((ph, pi) => (
                          <a key={pi} href={ph.url ? `${process.env.REACT_APP_BACKEND_URL}${ph.url}` : ph.data}
                            target="_blank" rel="noreferrer"
                            className="text-[11px] px-2 py-1 rounded-lg bg-lm-sandsoft text-lm-brown inline-flex items-center gap-1">
                            <Camera className="w-3 h-3" /> {ph.name || `Allegato ${pi + 1}`}
                          </a>
                        ))}
                      </div>
                    )}
                    {o.exec_status === "completata" ? (
                      <div className="flex gap-2 mt-2.5">
                        <div className="flex-1 h-11 rounded-xl bg-[#EFF7F2] border border-[#9AD4AC] text-[#166534] font-semibold flex items-center justify-center gap-2">
                          <CheckCircle2 className="w-5 h-5" /> Completata
                        </div>
                        <Button data-testid={`undo-op-${o.code}`} onClick={() => setExecStatus(o, "da_effettuare")}
                          variant="outline" className="h-11 border-lm-sand text-lm-brown">Annulla spunta</Button>
                      </div>
                    ) : (
                      <div className="flex gap-2 mt-2.5">
                        <Button data-testid={`complete-op-${o.code}`} onClick={() => setExecStatus(o, "completata")}
                          className="flex-1 h-11 bg-[#166534] hover:bg-[#12532b] text-white">
                          <CheckCircle2 className="w-5 h-5 mr-2" /> ✓ Completata
                        </Button>
                        <Button data-testid={`problem-op-${o.code}`} onClick={() => onExec(o)}
                          variant="outline" className="h-11 border-[#9A6B00] text-[#9A6B00]">
                          <AlertTriangle className="w-4 h-4 mr-1.5" /> Segnala problema
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const STATUSES = [
  { k: "completata", label: "Completata", cls: "bg-[#166534] text-white" },
  { k: "parziale", label: "Parziale", cls: "bg-[#9A6B00] text-white" },
  { k: "in_corso", label: "In corso", cls: "bg-[#1D4ED8] text-white" },
  { k: "non_effettuata", label: "Non effettuata", cls: "bg-[#991B1B] text-white" },
];

function ExecDialog({ op, onClose, onDone, offline }) {
  const [status, setStatus] = useState(op.exec_status && !["da_effettuare", "completata"].includes(op.exec_status) ? op.exec_status : "parziale");
  const [quantity, setQuantity] = useState(op.exec_quantity || "");
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const onFile = (e) => {
    const fs = Array.from(e.target.files || []);
    if (fs.length) setFiles((prev) => [...prev, ...fs]);
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, j) => j !== i));

  const submit = async () => {
    setSaving(true);
    try {
      const photos = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photos.push({ id: data.id, url: data.url, name: data.name, content_type: data.content_type });
      }
      const body = { exec_status: status, exec_quantity: quantity || null, exec_notes: notes || null, exec_reason: reason || null, photos };
      await api.patch(`/requests/${op.id}/exec`, body);
      toast.success("Operazione aggiornata");
      onClose(); onDone();
    } catch (e) {
      if (offline) {
        const body = { exec_status: status, exec_quantity: quantity || null, exec_notes: notes || null, exec_reason: reason || null, photos: [] };
        const q = JSON.parse(localStorage.getItem("lm_pending_exec") || "[]");
        q.push({ id: op.id, body });
        localStorage.setItem("lm_pending_exec", JSON.stringify(q));
        toast.message("Salvato offline (foto escluse), verrà inviato al ritorno della rete");
        onClose();
      } else toast.error(apiErr(e));
    } finally { setSaving(false); }
  };

  const problem = status === "non_effettuata" || status === "parziale";

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-lm-cream" data-testid="exec-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl text-lm-brown">{op.code} · Segnala problema</DialogTitle>
          <DialogDescription className="text-lm-muted text-sm">Registra parziali, mancate consegne, note o foto dell'operazione.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-2">
            {STATUSES.map((s) => (
              <button key={s.k} data-testid={`exec-${s.k}`} onClick={() => setStatus(s.k)}
                className={`h-14 rounded-xl font-semibold text-sm transition-all ${status === s.k ? s.cls + " ring-2 ring-offset-2 ring-lm-brown" : "bg-white border border-lm-sandsoft text-lm-text2"}`}>
                {s.label}
              </button>
            ))}
          </div>
          <div><label className="text-xs text-lm-text2 font-medium">Quantità effettiva (per parziali)</label>
            <Input data-testid="exec-quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="es. 3 colli su 5" className="mt-1 bg-white h-12" /></div>
          {problem && <div><label className="text-xs text-lm-text2 font-medium">Motivazione (fornitore chiuso, merce non pronta, danni…)</label>
            <Textarea data-testid="exec-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 bg-white" rows={2} /></div>}
          <div><label className="text-xs text-lm-text2 font-medium">Note</label>
            <Textarea data-testid="exec-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 bg-white" rows={2} /></div>
          <label className="flex items-center gap-2 h-12 rounded-xl border border-lm-sandsoft bg-white px-4 cursor-pointer text-lm-text2">
            <Camera className="w-5 h-5" /> {files.length ? `${files.length} file selezionati — aggiungi altri` : "Scatta / allega foto (merce o DDT)"}
            <input type="file" accept="image/*,application/pdf" capture="environment" multiple onChange={onFile} className="hidden" data-testid="exec-photo" />
          </label>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="exec-file-list">
              {files.map((f, i) => (
                <span key={i} className="text-[11px] px-2 py-1 rounded-lg bg-lm-sandsoft text-lm-brown inline-flex items-center gap-1">
                  {f.name}<button onClick={() => removeFile(i)} className="text-[#991B1B] font-bold ml-0.5">×</button>
                </span>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-lm-sand text-lm-brown h-12">Annulla</Button>
          <Button data-testid="exec-submit" onClick={submit} disabled={saving} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream h-12">Salva aggiornamento</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
